import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toId } from "../../lib/subjects";
import { getRequestStatusLabel } from "../../lib/requestStatus";

import { listBookingsForUser } from "../../data/bookings";
import { listMyRequests } from "../../data/requests";
import { listTutors } from "../../data/tutors";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

const absUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${API_BASE}${u.startsWith("/") ? "" : "/"}${u}`;
};


const theme = {
  bg: "#272c39",
  primary: "#7269e3",
  secondary: "#a783a6",
  success: "#98dea3",
};

const fmt = (iso, lng) =>
  new Date(iso).toLocaleString(lng, { dateStyle: "medium", timeStyle: "short" });

function safeTs(iso) {
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function mapTutorFromApi(u, t) {
  return {
    id: u?.id,
    name: u?.profile?.displayName || u?.name || t("roles.tutor", { defaultValue: "Tutor" }),
    subjects: Array.isArray(u?.profile?.subjects) ? u.profile.subjects : [],
    rate: typeof u?.profile?.rate === "number" ? u.profile.rate : null,
    city: u?.profile?.city || "",
    avatar: absUrl(u?.profile?.avatarUrl) || "",
  };
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language || "en";

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const displayName = (auth?.name || "").trim();

  /* ------------------ BOOKINGS ------------------ */
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsError, setBookingsError] = useState("");

  /* ------------------ REQUESTS (student) ------------------ */
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsError, setRequestsError] = useState("");

  /* ------------------ TUTORS ------------------ */
  const [recommended, setRecommended] = useState([]);
  const [tutorById, setTutorById] = useState({});

  const refresh = useCallback(async () => {
    setLoadingBookings(true);
    setLoadingRequests(true);
    setBookingsError("");
    setRequestsError("");

    try {
      const [bookingsData, requestsData] = await Promise.all([
        listBookingsForUser(),
        listMyRequests(),
      ]);

      setBookings(Array.isArray(bookingsData) ? bookingsData : []);
      setRequests(Array.isArray(requestsData) ? requestsData : []);
    } catch (e) {
      console.error("Failed to refresh dashboard:", e);
      setBookings([]);
      setRequests([]);
      setBookingsError(
        t("errors.loadBookings", { defaultValue: "Failed to load bookings" })
      );
      setRequestsError(
        t("errors.loadRequests", { defaultValue: "Failed to load requests" })
      );
    } finally {
      setLoadingBookings(false);
      setLoadingRequests(false);
    }
  }, [t]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!auth?.id) return;
      if (!alive) return;
      await refresh();
    })();

    return () => {
      alive = false;
    };
  }, [auth?.id, refresh]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await listTutors({ limit: 20 });
        if (!alive) return;

        const rawItems = Array.isArray(res) ? res : res?.items ?? [];
        const items = rawItems.map((u) => mapTutorFromApi(u, t)).filter((x) => x?.id);

        const byId = {};
        for (const tu of items) byId[String(tu.id)] = tu;

        setTutorById(byId);
        setRecommended(items.slice(0, 3));
      } catch (e) {
        console.error("Failed to load tutors:", e);
        if (!alive) return;
        setRecommended([]);
        setTutorById({});
      }
    })();

    return () => {
      alive = false;
    };
  }, [lng, t]);

  const DAY = 24 * 60 * 60 * 1000;

  const upcoming7 = useMemo(() => {
    const now = Date.now();
    const arr = Array.isArray(bookings) ? bookings : [];
    const maxTs = now + 7 * DAY;

    return arr
      .filter((b) => {
        const ts = safeTs(b?.startISO);
        if (ts === null) return false;
        if (ts < now) return false;
        if (ts > maxTs) return false;
        return !["declined", "cancelled"].includes(String(b?.status || ""));
      })
      .sort((a, b) => (safeTs(a?.startISO) ?? 0) - (safeTs(b?.startISO) ?? 0))
      .slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const recentRequests = useMemo(() => {
    const now = Date.now();
    const arr = Array.isArray(requests) ? requests : [];
    const cutoff = now - 5 * DAY;

    return arr
      .filter((r) => {
        const st = String(r?.status || "").toUpperCase();
        if (st !== "NEW") return false;

        const ts = safeTs(r?.updatedAt || r?.createdAt);
        if (ts === null) return true;
        return ts >= cutoff;
      })
      .sort((a, b) => {
        const ta = safeTs(a?.updatedAt || a?.createdAt) ?? 0;
        const tb = safeTs(b?.updatedAt || b?.createdAt) ?? 0;
        return tb - ta;
      })
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests]);

  return (
    <main className="min-h-screen" style={{ background: theme.bg, color: "white" }}>
      <div className="mx-auto max-w-6xl px-4 pt-10 pb-24">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-unbounded text-3xl md:text-4xl font-extrabold tracking-tight">
              {t("dashboard.welcome", {
                defaultValue: "Welcome back{{name}}",
                name: displayName ? `, ${displayName}` : "",
              })}
            </h1>

            <p className="mt-1 text-white/80">
              {t("dashboard.subtitle", {
                defaultValue: "Your lessons and requests in one place.",
              })}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/search"
              className="rounded-xl px-4 py-2 font-semibold hover:brightness-110"
              style={{ background: theme.primary }}
            >
              {t("actions.findTutor", { defaultValue: "Find a tutor" })}
            </Link>

            <button
              onClick={() => navigate("/bookings")}
              className="rounded-xl border border-white/20 px-4 py-2 hover:bg-white/5"
            >
              {t("actions.myBookings", { defaultValue: "My bookings" })}
            </button>

            <button
              onClick={refresh}
              className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-white/80 hover:bg-white/[0.07]"
            >
              {t("actions.refresh", { defaultValue: "Refresh" })}
            </button>
          </div>
        </header>

        <section className="mt-8 grid gap-6 lg:grid-cols-3">
          {/* min-w-0: grid children default to min-width:auto and refuse to
              shrink below their content's intrinsic width, which pushed this
              grid wider than small phone screens (content then gets clipped). */}
          <div className="min-w-0 lg:col-span-2 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-unbounded text-xl font-semibold">
                  {t("dashboard.recentRequests", { defaultValue: "Recent requests" })}
                </h2>
                <p className="mt-1 text-sm text-white/60">
                  {t("dashboard.recentRequestsHint", {
                    defaultValue:
                      "We show pending requests for a few days to keep things tidy.",
                  })}
                </p>
              </div>

              <button
                onClick={() => navigate("/my-requests")}
                className="text-sm text-white/80 hover:text-white"
              >
                {t("dashboard.seeAll", { defaultValue: "See all" })}
              </button>
            </div>

            {loadingRequests ? (
              <LoadingCard />
            ) : requestsError ? (
              <ErrorCard text={requestsError} />
            ) : recentRequests.length === 0 ? (
              <EmptyState
                title={t("dashboard.noRequests", { defaultValue: "No requests yet" })}
                subtitle={t("dashboard.noRequestsHint", {
                  defaultValue: "Send a request to a tutor and you’ll see the status here.",
                })}
                cta={t("actions.findTutor", { defaultValue: "Find a tutor" })}
                onClick={() => navigate("/search")}
              />
            ) : (
              <ul className="mt-4 space-y-3">
                {recentRequests.map((r) => {
                  const subj = r?.subject || "General";
                  const subjectLabel = t(`subjects.${toId(subj)}`, { defaultValue: subj });

                  const tutorName =
                    r?.tutorName || t("roles.tutor", { defaultValue: "Tutor" });
                  const pref = r?.preferredAt ? fmt(r.preferredAt, lng) : null;

                  return (
                    <li
                      key={r.id}
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3 hover:bg-white/[0.07] transition"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            <span className="text-white/90">{subjectLabel}</span>{" "}
                            <span className="text-white/60">·</span>{" "}
                            <span className="text-white/80">{tutorName}</span>
                          </div>

                          <div className="mt-1 text-sm text-white/65">
                            {pref ? (
                              <span>
                                🕒 {t("requests.preferredAt", { defaultValue: "Бажаний час" })}:{" "}
                                {pref}
                              </span>
                            ) : (
                              <span>
                                🕒{" "}
                                {t("requests.preferredMissing", {
                                  defaultValue: "Preferred time not set",
                                })}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <Tag subtle>{getRequestStatusLabel(t, r?.status)}</Tag>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2 flex-wrap justify-end">
                          <span className="rounded-xl border border-white/15 px-3 py-2 text-sm text-white/70">
                            {t("requests.waitingTutor", {
                              defaultValue: "Waiting for confirmation",
                            })}
                          </span>

                          <button
                            onClick={() => navigate(`/requests/${r.id}`)}
                            className="rounded-xl border border-white/20 px-4 py-2 text-sm hover:bg-white/5"
                          >
                            {t("dashboard.view", { defaultValue: "View" })}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="min-w-0 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
            <h2 className="font-unbounded text-xl font-semibold">
              {t("dashboard.recommended", { defaultValue: "Recommended tutors" })}
            </h2>

            <div className="mt-4 space-y-3">
              {recommended.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-white/70">
                  {t("dashboard.noRecommendations", {
                    defaultValue: "No recommendations yet.",
                  })}
                </div>
              ) : (
                recommended.map((tutor) => <Recommendation key={tutor.id} tutor={tutor} />)
              )}
            </div>

            <Link
              to="/search"
              className="mt-4 inline-block w-full rounded-xl px-4 py-2 text-center font-semibold hover:brightness-110"
              style={{ background: theme.primary }}
            >
              {t("dashboard.browseAll", { defaultValue: "Browse all tutors" })}
            </Link>
          </div>
        </section>

        <section className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-unbounded text-xl font-semibold">
                {t("dashboard.upcoming", { defaultValue: "Upcoming lessons" })}
              </h2>
              <p className="mt-1 text-sm text-white/60">
                {t("dashboard.upcomingHint7d", {
                  defaultValue: "Only lessons in the next 7 days show here.",
                })}
              </p>
            </div>

            <button
              onClick={() => navigate("/bookings")}
              className="text-sm text-white/80 hover:text-white"
            >
              {t("dashboard.seeAll", { defaultValue: "See all" })}
            </button>
          </div>

          {loadingBookings ? (
            <LoadingCard />
          ) : bookingsError ? (
            <ErrorCard text={bookingsError} />
          ) : upcoming7.length === 0 ? (
            <EmptyState
              title={t("dashboard.noLessons", { defaultValue: "No upcoming lessons" })}
              subtitle={t("dashboard.noLessonsHint", {
                defaultValue: "Once a time is scheduled, your lesson will appear here.",
              })}
              cta={t("actions.findTutor", { defaultValue: "Find a tutor" })}
              onClick={() => navigate("/search")}
            />
          ) : (
            <ul className="mt-4 space-y-3">
              {upcoming7.map((b) => {
                const tu = tutorById[String(b.tutorId)] || null;
                const avatar = tu?.avatar || "";
                const displayTutorName =
                  tu?.name || b.tutorName || t("roles.tutor", { defaultValue: "Tutor" });

                const modeKey = b.mode === "in-person" ? "inPerson" : b.mode || "online";
                const modeLabel = t(`modes.${modeKey}`, {
                  defaultValue: b.mode || "online",
                });

                const subj = b.subject || "General";
                const subjectLabel = t(`subjects.${toId(subj)}`, { defaultValue: subj });

                const rateNum = Number(b?.rate);
const rateLabel =
  Number.isFinite(rateNum)
    ? `${rateNum.toLocaleString("uk-UA")} ₴/год`
    : "Ціна за запитом";

                return (
                  <li
                    key={b.id}
                    className="flex items-center justify-between rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {avatar && !avatar.endsWith("/") ? (
                        <img
                          crossOrigin="anonymous"
                          src={avatar}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }}
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-white/20"
                          loading="lazy"
                          alt=""
                        />
                      ) : (
                        <div className="h-10 w-10 shrink-0 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white/35">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                          </svg>
                        </div>
                      )}

                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {displayTutorName} •{" "}
                          <span className="text-white/80">{subjectLabel}</span>
                        </p>
                        <p className="text-sm text-white/70">
                          {fmt(b.startISO, lng)} • {modeLabel} • {rateLabel}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          navigate(
                            `/chat/${b.tutorId}?name=${encodeURIComponent(displayTutorName)}`
                          )
                        }
                        className="rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/5"
                      >
                        {t("actions.openChat", { defaultValue: "Open chat" })}
                      </button>

                      <button
                        onClick={() => navigate("/bookings")}
                        className="rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/5"
                      >
                        {t("dashboard.manage", { defaultValue: "Manage" })}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

/* ---------- small UI helpers ---------- */
function LoadingCard() {
  const { t } = useTranslation();
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-6 text-white/80">
      {t("common.loading", { defaultValue: "Loading…" })}
    </div>
  );
}

function ErrorCard({ text }) {
  return (
    <div className="mt-4 rounded-xl border border-red-400/20 bg-red-500/10 p-6 text-red-100">
      {text}
    </div>
  );
}

function EmptyState({ title, subtitle, cta, onClick }) {
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-6 text-center">
      <p className="text-white/90 font-medium">{title}</p>
      {subtitle ? <p className="mt-1 text-sm text-white/60">{subtitle}</p> : null}
      {cta ? (
        <button
          onClick={onClick}
          className="mt-3 rounded-xl px-4 py-2 font-semibold hover:brightness-110"
          style={{ background: theme.primary }}
        >
          {cta}
        </button>
      ) : null}
    </div>
  );
}

function Tag({ children, subtle, color }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-xs border"
      style={{
        borderColor: "rgba(255,255,255,0.12)",
        background: subtle
          ? "rgba(255,255,255,0.04)"
          : color
            ? `${color}22`
            : "rgba(255,255,255,0.08)",
        color: color ? color : subtle ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.9)",
      }}
    >
      {children}
    </span>
  );
}

function Recommendation({ tutor }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const displayName = tutor?.name || t("roles.tutor", { defaultValue: "Tutor" });
  const subjects = Array.isArray(tutor?.subjects) ? tutor.subjects : [];
  const subjectsLocalized = subjects.map((s) =>
    t(`subjects.${toId(s)}`, { defaultValue: s })
  );

  const priceNum = Number(tutor?.rate);
  const hasPrice = Number.isFinite(priceNum);
  const avatar = tutor?.avatar || "";

  return (
    <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-3 hover:bg-white/[0.07] transition">
      {/* flex-wrap lets the View button drop to its own line on narrow phones
          instead of forcing the row (and the whole dashboard grid) offscreen */}
      <div className="flex flex-wrap items-center gap-3">
        {avatar && !avatar.endsWith("/") ? (
          <img
            crossOrigin="anonymous"
            src={avatar}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }}
            alt=""
            className="h-10 w-10 rounded-full object-cover ring-2 ring-white/20"
            loading="lazy"
          />
        ) : (
          <div className="h-10 w-10 shrink-0 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white/35">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
            </svg>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium text-white/90">{displayName}</p>
            {hasPrice ? (
              <span className="shrink-0 rounded-full px-2.5 py-1 text-xs border border-white/10 bg-[#98dea3]/20 text-[#98dea3]">
                {`${priceNum.toLocaleString("uk-UA")} ₴/год`}
              </span>
            ) : null}
          </div>

          <p className="mt-0.5 text-sm text-white/65 truncate">
            {subjectsLocalized.length
              ? subjectsLocalized.join(", ")
              : t("search.noSubjects", { defaultValue: "No subjects yet" })}
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate(`/tutor/${tutor.id}`)}
          className="shrink-0 rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
        >
          {t("dashboard.view", { defaultValue: "View" })}
        </button>
      </div>
    </div>
  );
}
